import React, { Component } from 'react';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import debounce from 'lodash/debounce';
import pickBy from 'lodash/pickBy';
import PropTypes from 'prop-types';
import {
  Button,
  Dimmer,
  Divider,
  Form,
  Header,
  Icon,
  Loader,
  Message,
  Modal,
  Select,
  Segment,
} from 'semantic-ui-react';

import { planType } from '../types';

import FormOptionsFormatOutput from './formOptionsFormatOutput';
import FormOptionsCallback from './formOptionsCallback';

import { sendGetPlan } from '../../modules/account';
import {
  postRecognition,
} from '../../modules/recognition';

const ProtocalMatch = /^https?:\/\//i
const BasicUrlMatch = /https?:\/\/\S+\.\S+/i

const initialState = {
  open: false,
  uploadType: 'file',
  data: {
    speakers: 2,
  },
};

class UploadModal extends Component {
  constructor() {
    super();

    this.testAudio = document.createElement('audio');
    this.state = { ...initialState };
  }

  setUploadType = (type) => {
    this.setState({
      uploadType: type,
    });
  }

  resetState = () => {
    this.setState({ ...initialState });
  }

  handleSubmit = () => {
    const { connectedPostRecognition, error, postRecognitionRequestOut } = this.props;
    const { data } = this.state;
    if (!postRecognitionRequestOut) {
      /* TODO: resolve what happens if file and mediaurl */
      /* Remove items that are empty strings */
      const cleanData = pickBy(data, x => x !== '');
      connectedPostRecognition(cleanData).then(() => {
        if (!error) {
          this.resetState();
        }
      });
    }
  }

  updateDataField = (field, value, subField) => {
    this.setState((prevState) => {
      const newData = { ...prevState.data };
      let oldValue = value;
      if (subField) {
        const newValue = { ...newData[field] };
        newValue[subField] = oldValue;
        oldValue = newValue;
      }
      newData[field] = oldValue;
      return { data: newData };
    });
  }

  /**
   * If a user has a plan, treat as a regular
   * user and check that the plan state is not
   * 'canceled'.
   *
   * If a user doesn't have a plan, treat as
   * legacy app user and treat as enabled.
   */
  accountIsEnabled = () => {
    const { plan } = this.props;
    return plan ? plan.state !== 'canceled' : true;
  }

  validSubmission = () => {
    const { data, uploadType } = this.state;
    return data[uploadType];
  }

  openModal = () => {
    const { connectedSendGetPlan } = this.props;
    this.setState({
      open: true,
    });
    connectedSendGetPlan();
  }

  closeModal = () => {
    this.resetState();
  }

  testMediaUrl = debounce((rawUrl) => {
    const audio = this.testAudio;
    let url = rawUrl;
    audio.src = '';
    this.setState({
      invalidMediaUrl: false,
    });
    audio.onerror = () => {
      this.setState({
        invalidMediaUrl: !!rawUrl, // If input field empty, don't show an error
      });
    };
    if (!url.match(ProtocalMatch)) {
      url = `http://${url}`;
    }
    if (!url.match(BasicUrlMatch)) {
      return;
    }
    audio.src = url;
  }, 500);

  render() {
    const { getPlanRequestOut, postRecognitionRequestOut } = this.props;
    const {
      data, open, uploadType, invalidMediaUrl,
    } = this.state;
    return (
      <Modal
        open={open}
        size="tiny"
        trigger={(
          <Button onClick={this.openModal} primary>
            Create Recognition
          </Button>
        )}
      >
        <Modal.Header>
          Create Recognition
        </Modal.Header>
        <Modal.Content scrolling className="upload-modal-content">
          <Dimmer active={getPlanRequestOut} inverted>
            <Loader active={getPlanRequestOut} />
          </Dimmer>
          { !this.accountIsEnabled()
              && (
                <Message warning>
                  <Message.Header content="Account Disabled" />
                  <Message.Content>
                    This account is unable to create further recognitions.
                    <br />
                    Please contact
                    <a href="mailto:support@remeeting.com"> support </a>
                    for assistance.
                  </Message.Content>
                </Message>
              )
          }

          <Form>
            <Header as="h3" textAlign="center">
              Media
            </Header>
            <Form.Group>
              <Button.Group>
                <Button
                  positive={uploadType === 'file'}
                  onClick={() => { this.updateDataField('mediaUrl', ''); this.testMediaUrl(''); this.setUploadType('file'); }}
                >
                  File Upload
                </Button>
                <Button.Or />
                <Button
                  positive={uploadType === 'mediaUrl'}
                  onClick={() => { this.setUploadType('mediaUrl'); }}
                >
                  Media URL
                </Button>
              </Button.Group>
            </Form.Group>
            { uploadType === 'file'
                && (
                  <Form.Input
                    type="file"
                    content="Select File"
                    onChange={(event) => { this.updateDataField('file', event.target.files[0]); }}
                  />
                )
            }
            { uploadType === 'mediaUrl'
                && (
                <Form.Input
                  onChange={(event, eventData) => { this.updateDataField('mediaUrl', eventData.value); this.testMediaUrl(eventData.value); }}
                  label="Media URL"
                  type="text"
                  error={invalidMediaUrl}
                  value={data.mediaUrl || ''}
                />
                )
            }
            { uploadType === 'mediaUrl' && invalidMediaUrl
                && (
                  <Segment>
                    <Icon name="info circle" color="orange" />
                    The provided URL appears to be invalid.
                    <br />
                    This may cause the recognition to fail to process.
                  </Segment>
                )
            }
            <Header as="h3" textAlign="center">
              Speakers
            </Header>
            <Form.Field
              control={Select}
              label="Number of Speakers"
              value={data.speakers}
              onChange={(event, eventData) => { this.updateDataField('speakers', eventData.value); }}
              options={[
                { text: 'Uncertain', value: 0, key: '0' },
                { text: '1', value: 1, key: '1' },
                { text: '2', value: 2, key: '2' },
                { text: '3', value: 3, key: '3' },
                { text: '4', value: 4, key: '4' },
                { text: '5', value: 5, key: '5' },
                { text: '6+', value: 6, key: '6' },
              ]}
            />
            <Divider hidden />
            <FormOptionsFormatOutput
              data={data}
              updateDataField={this.updateDataField}
            />
            <Divider hidden />

            <FormOptionsCallback
              data={data}
              updateDataField={this.updateDataField}
            />
          </Form>
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={this.closeModal} disabled={postRecognitionRequestOut}>
            Cancel
          </Button>
          <Button
            primary
            onClick={this.handleSubmit}
            loading={postRecognitionRequestOut}
            disabled={!(this.accountIsEnabled() && this.validSubmission())}
          >
            Create Recognition
          </Button>
        </Modal.Actions>
      </Modal>
    );
  }
}

UploadModal.propTypes = {
  error: PropTypes.shape({}),
  getPlanRequestOut: PropTypes.bool.isRequired,
  plan: planType,
  postRecognitionRequestOut: PropTypes.bool.isRequired,
  connectedPostRecognition: PropTypes.func.isRequired,
  connectedSendGetPlan: PropTypes.func.isRequired,
};

UploadModal.defaultProps = {
  error: undefined,
  plan: undefined,
};

const mapStateToProps = state => ({
  error: state.recognitions.error,
  getPlanRequestOut: state.account.getPlanRequestOut,
  plan: state.account.plan,
  postRecognitionRequestOut: state.recognition.postRecognitionRequestOut,
});

const mapDispatchToProps = dispatch => bindActionCreators({
  connectedPostRecognition: postRecognition,
  connectedSendGetPlan: sendGetPlan,
}, dispatch);

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(UploadModal);
